import { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { getTeamMemberIds } from '../utils/teamUtils';

const OrderContext = createContext();

export function OrderProvider({ children }) {
  const [orders, setOrders] = useState([]);
  const [draftOrders, setDraftOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draftLoading, setDraftLoading] = useState(false);
  const { currentAgent } = useAuth();

  // Load orders and draft orders for current agent
  useEffect(() => {
    if (currentAgent?.id) {
      loadOrders();
      loadDraftOrders();
    }
  }, [currentAgent]);

  const loadOrders = async () => {
    if (!currentAgent?.id) return;
    
    try {
      setLoading(true);
      const ordersRef = collection(db, 'orders');
      const role = currentAgent?.role || 'user';
      const agentId = currentAgent?.agent_id || currentAgent?.id;
      
      let q;
      if (role === 'admin') {
        // Admin: get all orders
        q = query(ordersRef, orderBy('created_at', 'desc'));
      } else if (role === 'teamLeader') {
        // Team Leader: get orders for all team members
        const teamMemberIds = await getTeamMemberIds(agentId);
        
        if (teamMemberIds.length === 0) {
          setOrders([]);
          setLoading(false);
          return;
        }
        
        // Handle Firestore 'in' operator limit (10 values max)
        if (teamMemberIds.length <= 10) {
          q = query(
            ordersRef,
            where('agent_id', 'in', teamMemberIds),
            orderBy('created_at', 'desc')
          );
        } else {
          // Batch queries for large teams
          const batches = [];
          for (let i = 0; i < teamMemberIds.length; i += 10) {
            const batch = teamMemberIds.slice(i, i + 10);
            const batchQuery = query(
              ordersRef,
              where('agent_id', 'in', batch),
              orderBy('created_at', 'desc')
            );
            batches.push(getDocs(batchQuery));
          }
          
          const results = await Promise.all(batches);
          const allOrders = [];
          results.forEach(snapshot => {
            snapshot.docs.forEach(doc => {
              allOrders.push({ id: doc.id, ...doc.data() });
            });
          });
          
          // Sort by created_at desc
          allOrders.sort((a, b) => {
            const dateA = a.created_at?.toDate?.() || new Date(a.created_at);
            const dateB = b.created_at?.toDate?.() || new Date(b.created_at);
            return dateB - dateA;
          });
          
          setOrders(allOrders);
          setLoading(false);
          return;
        }
      } else if (role === 'referrer') {
        // Referrer: filter by referrer field
        q = query(
          ordersRef,
          where('referrer', '==', agentId),
          orderBy('created_at', 'desc')
        );
      } else {
        // User: filter by agent_id
        q = query(
          ordersRef,
          where('agent_id', '==', agentId),
          orderBy('created_at', 'desc')
        );
      }
      
      const querySnapshot = await getDocs(q);
      const ordersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading orders:', error);
      // Set empty array on error to prevent crashes
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDraftOrders = async () => {
    if (!currentAgent?.id) return;
    
    try {
      setDraftLoading(true);
      const draftOrdersRef = collection(db, 'draft_orders');
      const role = currentAgent?.role || 'user';
      const agentId = currentAgent?.agent_id || currentAgent?.id;
      
      let q;
      if (role === 'admin') {
        // Admin: get all draft orders
        q = query(draftOrdersRef, orderBy('createdAt', 'desc'));
      } else if (role === 'teamLeader') {
        // Team Leader: get draft orders for all team members
        const teamMemberIds = await getTeamMemberIds(agentId);
        
        if (teamMemberIds.length === 0) {
          setDraftOrders([]);
          setDraftLoading(false);
          return;
        }
        
        // Handle Firestore 'in' operator limit (10 values max)
        if (teamMemberIds.length <= 10) {
          q = query(
            draftOrdersRef,
            where('agent_id', 'in', teamMemberIds),
            orderBy('createdAt', 'desc')
          );
        } else {
          // Batch queries for large teams
          const batches = [];
          for (let i = 0; i < teamMemberIds.length; i += 10) {
            const batch = teamMemberIds.slice(i, i + 10);
            const batchQuery = query(
              draftOrdersRef,
              where('agent_id', 'in', batch),
              orderBy('createdAt', 'desc')
            );
            batches.push(getDocs(batchQuery));
          }
          
          const results = await Promise.all(batches);
          const allDraftOrders = [];
          results.forEach(snapshot => {
            snapshot.docs.forEach(doc => {
              const data = doc.data();
              // Derive expiration status
              const now = new Date();
              const expiresAt = data.expiresAt ? new Date(data.expiresAt) : null;
              const isExpired = expiresAt && now > expiresAt;
              
              allDraftOrders.push({ 
                id: doc.id, 
                ...data,
                isExpired,
                expiresAt: expiresAt?.toISOString()
              });
            });
          });
          
          // Sort by createdAt desc
          allDraftOrders.sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
            return dateB - dateA;
          });
          
          setDraftOrders(allDraftOrders);
          setDraftLoading(false);
          return;
        }
      } else {
        // User: filter by agent_id
        q = query(
          draftOrdersRef,
          where('agent_id', '==', agentId),
          orderBy('createdAt', 'desc')
        );
      }
      
      const querySnapshot = await getDocs(q);
      const draftOrdersData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Derive expiration status
        const now = new Date();
        const expiresAt = data.expiresAt ? new Date(data.expiresAt) : null;
        const isExpired = expiresAt && now > expiresAt;
        
        return {
          id: doc.id,
          ...data,
          isExpired,
          expiresAt: expiresAt?.toISOString()
        };
      });
      
      setDraftOrders(draftOrdersData);
    } catch (error) {
      console.error('Error loading draft orders:', error);
      setDraftOrders([]);
    } finally {
      setDraftLoading(false);
    }
  };

  const getOrderById = (orderId) => {
    return orders.find(order => order.id === orderId);
  };

  const getDraftOrderById = (draftId) => {
    return draftOrders.find(draft => draft.id === draftId);
  };

  const refreshOrders = () => {
    loadOrders();
  };

  const refreshDraftOrders = () => {
    loadDraftOrders();
  };

  return (
    <OrderContext.Provider
      value={{
        orders,
        draftOrders,
        loading,
        draftLoading,
        loadOrders,
        loadDraftOrders,
        getOrderById,
        getDraftOrderById,
        refreshOrders,
        refreshDraftOrders
      }}
    >
      {children}
    </OrderContext.Provider>
  );
}

export function useOrders() {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
}
